import React, { useState } from 'react';
import {
	Share2,
	Instagram,
	Facebook,
	Linkedin,
	Edit3,
	Check,
	X,
	Search,
	User,
	Lock,
	Globe,
	Monitor,
	Key,
} from 'lucide-react';
import Card from '../../../../components/ui/card';
import Button from '../../../../components/ui/button';
import Input from '../../../../components/ui/input';
import { Textarea } from '../../../../components/ui/textarea';

const SocialMediaCard = ({ client, clientDataHook }) => {
	const { updateField, isEditMode, toggleEditMode, cancelEdit } =
		clientDataHook;
	const [editValues, setEditValues] = useState({});

	const fields = [
		{
			key: 'instagramLink',
			label: 'Instagram',
			type: 'url',
			value: client?.instagramLink || '',
			icon: Instagram,
			placeholder: 'https://instagram.com/yourusername',
		},
		{
			key: 'facebookLink',
			label: 'Facebook',
			type: 'url',
			value: client?.facebookLink || '',
			icon: Facebook,
			placeholder: 'https://facebook.com/yourpage',
		},
		{
			key: 'googleBusinessUrl',
			label: 'Google Business',
			type: 'url',
			value: client?.googleBusinessUrl || '',
			icon: Search,
			placeholder: 'https://business.google.com/yourprofile',
		},
		{
			key: 'linkedinLink',
			label: 'LinkedIn',
			type: 'url',
			value: client?.linkedinLink || '',
			icon: Linkedin,
			placeholder: 'https://linkedin.com/in/yourprofile',
		},
		{
			key: 'socialMediaCredentials',
			label: 'Social Credentials',
			type: 'textarea',
			value: client?.socialMediaCredentials || '',
			icon: Key,
			placeholder:
				'Instagram: @username / password123\nFacebook: username / password123',
		},
		{
			key: 'crmName',
			label: 'CRM',
			type: 'text',
			value: client?.crmName || '',
			icon: Monitor,
			placeholder: 'Salesforce, HubSpot, etc.',
		},
		{
			key: 'crmUsername',
			label: 'CRM Username',
			type: 'text',
			value: client?.crmUsername || '',
			icon: User,
			placeholder: 'Your CRM username',
		},
		{
			key: 'crmPassword',
			label: 'CRM Password',
			type: 'password',
			value: client?.crmPassword || '',
			icon: Lock,
			placeholder: 'Your CRM password',
		},
		{
			key: 'websiteUrl',
			label: 'Website URL',
			type: 'url',
			value: client?.websiteUrl || '',
			icon: Globe,
			placeholder: 'https://yourwebsite.com',
		},
		{
			key: 'websitePlatform',
			label: 'Website Platform',
			type: 'text',
			value: client?.websitePlatform || '',
			icon: Monitor,
			placeholder: 'WordPress, Squarespace, etc.',
		},
		{
			key: 'websiteCredentials',
			label: 'Website Credentials',
			type: 'textarea',
			value: client?.websiteCredentials || '',
			icon: Key,
			placeholder:
				'Username: admin\nPassword: password123\nURL: https://yoursite.com/wp-admin',
		},
	];

	// Login fields aren't kept in browser storage (#152), so right after a reload the
	// client list may not have them yet (undefined). Don't seed or save a blank for a
	// field that hasn't loaded; the input picks up the value once it arrives.
	const isUnloaded = (fieldKey) => client?.[fieldKey] === undefined;

	const handleEdit = (fieldKey) => {
		const next = { ...editValues };
		if (isUnloaded(fieldKey)) {
			delete next[fieldKey];
		} else {
			next[fieldKey] = fields.find((f) => f.key === fieldKey)?.value || '';
		}
		setEditValues(next);
		toggleEditMode(fieldKey);
	};

	const handleSave = async (fieldKey) => {
		const value = editValues[fieldKey] ?? (isUnloaded(fieldKey) ? undefined : client[fieldKey] || '');
		if (value === undefined) return;
		const success = await updateField(fieldKey, value);
		if (success) {
			setEditValues({ ...editValues, [fieldKey]: '' });
		}
	};

	const handleCancel = (fieldKey) => {
		setEditValues({ ...editValues, [fieldKey]: '' });
		cancelEdit(fieldKey);
	};

	const renderField = (field) => {
		const isEditing = isEditMode[field.key];
		const editValue = editValues[field.key] ?? field.value;
		const hasValue = field.value && field.value.trim() !== '';

		// Conditional styling: white background for filled fields, gray for unfilled
		const cardBgClass = hasValue ? 'bg-white shadow-sm' : 'bg-stone-50/50';

		return (
			<div
				key={field.key}
				className={`${cardBgClass} border border-gray-100 rounded-lg p-3 sm:p-4 transition-all duration-200 hover:border-primary/30`}>
				<div className='flex items-start justify-between'>
					<div className='flex-1'>
						<label className='flex items-center text-xs font-montserrat-medium uppercase tracking-wide text-gray-400 mb-1.5'>
							{field.icon && <field.icon className='w-3.5 h-3.5 mr-1.5 text-primary/30' />}
							{field.label}
						</label>

						{isEditing ? (
							<div className='space-y-3'>
								{field.type === 'textarea' ? (
									<Textarea
										value={editValue}
										onChange={(e) =>
											setEditValues({
												...editValues,
												[field.key]: e.target.value,
											})
										}
										placeholder={field.placeholder}
										className='w-full'
										rows={4}
									/>
								) : (
									<Input
										type={field.type}
										value={editValue}
										onChange={(e) =>
											setEditValues({
												...editValues,
												[field.key]: e.target.value,
											})
										}
										placeholder={field.placeholder}
										className='w-full'
									/>
								)}

								<div className='flex items-center space-x-2'>
									<Button size='sm' onClick={() => handleSave(field.key)}>
										<Check className='w-4 h-4 mr-1' />
										Save
									</Button>
									<Button
										size='sm'
										variant='outline'
										onClick={() => handleCancel(field.key)}>
										<X className='w-4 h-4 mr-1' />
										Cancel
									</Button>
								</div>
							</div>
						) : (
							<div className='text-sm font-montserrat-medium text-gray-900'>
								{field.value ? (
									field.type === 'url' ? (
										<a
											href={field.value}
											target='_blank'
											rel='noopener noreferrer'
											className='text-primary hover:text-primary/80 hover:underline break-all'>
											{field.value}
										</a>
									) : field.type === 'textarea' ? (
										<div className='whitespace-pre-wrap'>{field.value}</div>
									) : field.type === 'password' ? (
										<span className='font-mono'>{'•'.repeat(8)}</span>
									) : (
										field.value
									)
								) : (
									<span className='text-gray-300 italic font-montserrat-regular'>
										Not provided
									</span>
								)}
							</div>
						)}
					</div>

					{!isEditing && (
						<Button
							size='sm'
							variant='ghost'
							onClick={() => handleEdit(field.key)}
							aria-label={`Edit ${field.label}`}
							className='ml-2 text-gray-300 hover:text-primary flex-shrink-0 transition-colors duration-200'>
							<Edit3 className='w-4 h-4' />
						</Button>
					)}
				</div>
			</div>
		);
	};

	return (
		<Card className='border-l-2 border-l-primary/20'>
			<Card.Header className='border-b border-gray-100'>
				<Card.Title className='flex items-center text-sm sm:text-base font-glacial uppercase tracking-wider text-gray-900'>
					<Share2 className='w-4 h-4 mr-2 text-primary' />
					Social Media & Software
				</Card.Title>
			</Card.Header>
			<Card.Content>
				<div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
					{fields.map(renderField)}
				</div>
			</Card.Content>
		</Card>
	);
};

export default SocialMediaCard;
